import { Fragment, useState, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isOverFreeLimit } from '../../lib/limits';
import { trackUpload } from 'documents-lib/js/tracking/tracker';
import { List } from 'immutable';
import { UPLOAD_FILE_INDEX } from 'documents-lib/js/tracking/actions';
import { getLocationQuery } from 'SalesContentIndexUI/data/utils/router';
import { canWrite } from 'documents-ui/lib/permissions';
import AllowedDocumentTypes from '../../lib/AllowedDocumentTypes';
import { Button } from 'foundations-components/transitional/components/Button';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import DocumentsCreatePanel from './DocumentsCreatePanel';
import ReuploadConfirmationModal from './ReuploadConfirmationModal';
import styled from 'styled-components';
import { LockedIcon } from 'foundations-assets/icons/components/LockedIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HiddenInput = styled.input.withConfig({
  displayName: "DocumentsCreateButton__HiddenInput"
})(["display:none !important;visibility:hidden !important;"]);
const LoadableDocumentsUsageLimitButton = Loadable({
  loader: () => import( /* webpackChunkName: "upgrade-components" */'../upgradeComponents').then(({
    DocumentsUsageLimitButton
  }) => DocumentsUsageLimitButton),
  ErrorComponent: ({
    retry
  }) => /*#__PURE__*/_jsxs(Button, {
    use: "primary",
    onClick: retry,
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "headerButtons.createButton.uploadDocument"
    }), /*#__PURE__*/_jsx(LockedIcon, {})]
  })
});
const DocumentsCreateButton = ({
  usage,
  portalIsAtLimit,
  isReupload = false,
  documentMetadata,
  buttonSize = 'medium',
  buttonText = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "headerButtons.createButton.uploadDocument"
  }),
  buttonUse = 'primary',
  onClick,
  uploadFile,
  importFile,
  location
}) => {
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const _file = useRef(null);
  const isButtonDisabled = () => {
    const isOverLimit = isOverFreeLimit(usage) || portalIsAtLimit;
    return !canWrite() || Boolean(!isReupload && isOverLimit);
  };
  const getButtonTooltipTitle = () => {
    if (!canWrite()) {
      return isReupload ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "documentsUI.missingWriteScope.reupload"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "documentsUI.missingWriteScope.upload"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "headerButtons.overLimitTooltip",
      options: {
        limit: usage && usage.limit,
        count: usage && usage.count
      }
    });
  };
  const getCurrentFolderId = () => {
    const locationQuery = getLocationQuery(location);
    const folderId = locationQuery.folder;
    return folderId ? parseInt(folderId, 10) : undefined;
  };
  const handleClick = () => {
    setShowCreatePanel(true);
    if (onClick) onClick();
  };
  const handleChange = event => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) {
      return;
    }
    const files = List(Array.prototype.slice.call(fileList));
    files.forEach(file => uploadFile({
      file,
      folderId: getCurrentFolderId()
    }));
    trackUpload({
      action: UPLOAD_FILE_INDEX,
      source: 'LOCAL'
    });
  };
  if (!isReupload && canWrite() && isOverFreeLimit(usage)) {
    return /*#__PURE__*/_jsx(LoadableDocumentsUsageLimitButton, {
      buttonText: buttonText
    });
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Tooltip, {
      title: getButtonTooltipTitle(),
      disabled: !isButtonDisabled(),
      placement: "bottom",
      children: /*#__PURE__*/_jsx(Button, {
        disabled: isButtonDisabled(),
        onClick: () => {
          if (isReupload) {
            setShowConfirmationModal(true);
          } else {
            handleClick();
          }
        },
        use: buttonUse,
        size: buttonSize,
        "data-selenium-test": "index-upload-document-test",
        marginInlineStart: "300",
        children: buttonText
      })
    }), showCreatePanel && /*#__PURE__*/_jsx(DocumentsCreatePanel, {
      documentMetadata: documentMetadata,
      uploadFile: uploadFile,
      importFile: importFile,
      onButtonClick: () => setShowCreatePanel(false),
      isReupload: isReupload,
      handleLocalFileImport: () => {
        const inputElement = _file.current;
        if (inputElement) {
          inputElement.click();
        }
        setShowCreatePanel(false);
      }
    }), showConfirmationModal && /*#__PURE__*/_jsx(ReuploadConfirmationModal, {
      onConfirm: () => {
        setShowConfirmationModal(false);
        handleClick();
      },
      onCancel: () => {
        setShowConfirmationModal(false);
      }
    }), /*#__PURE__*/_jsx(HiddenInput, {
      ref: ref => {
        _file.current = ref;
      },
      type: "file",
      accept: AllowedDocumentTypes.join(','),
      multiple: true,
      onChange: handleChange
    })]
  });
};
export default DocumentsCreateButton;